import { tint } from "polished";
import { Children, type HTMLAttributes, type ReactElement, type ReactNode, forwardRef } from "react";

import style from "./styles/UserAvatar.module.scss";

import { getCssColor } from "@/app-utils/color.ts";
import { type User } from "@/graphql.ts";
import { getInitialsForUser } from "@/modules/users/utils/user.js";
import { BASE_COLOR_SET } from "@/settings/settings.js";

const DEFAULT_SIZE = 20;

interface IAvatarProps {
  user: User;
  size?: number;
}

// FIXME: Introduce new colors (background + opacity should be temporary)
function Avatar({ user, size = DEFAULT_SIZE }: IAvatarProps) {
  if (!user) {
    return null;
  }
  const initials = getInitialsForUser(user);

  const txtColor = getCssColor(BASE_COLOR_SET[user.id % 8]);
  const bgColor = tint(0.7, txtColor);

  const dynamicStyles = {
    width: size,
    height: size,
    borderRadius: Math.ceil(size / 2),
    backgroundColor: bgColor,
    color: txtColor,
    fontSize: Math.ceil(size / 2.25),
  };

  return (
    <div className={style.avatar} style={dynamicStyles}>
      <span className={style.initials}>{initials}</span>
    </div>
  );
}

const AvatarPile = forwardRef<HTMLDivElement, { children?: ReactNode; domProps?: HTMLAttributes<HTMLDivElement> }>(
  function AvatarPile({ children, domProps }, ref) {
    const childElements = Children.toArray(children).filter((c) => !!c); // remove child React placeholder and Boolean
    const width = childElements.reduce((acc: number, child, idx) => {
      let size = Number((child as ReactElement)?.props?.size || 0) || DEFAULT_SIZE;

      // Max 5 avatars
      if (idx > 4) {
        size = 0;
      }

      return acc + (idx > 0 ? (size * 55) / 100 : size);
    }, 0);

    return (
      <div className={style.pile} style={{ width: `${width}px` }} ref={ref} {...domProps}>
        {children}
      </div>
    );
  },
);

export { Avatar, AvatarPile };
